
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {SankeyLink, SankeyNode} from "@amcharts/amcharts4/charts"
import Vue from "vue"
import Component from "vue-class-component"
import {theme} from "@/theme"
import {Inject, Prop} from "vue-property-decorator"
import {DateRangeSelector} from "@/DateRangeSelector"
import {cubejsApi, QueryData} from "@/cube/cubejsApi"
import {PlanService} from "@/superannuation/PlanService"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import format from "date-fns/format"
import {Dimension, MemberSnapshotMeasure, SuperScoreDimension} from "@/cube"
import {QueryOrderDirection} from "@/cube/cubejs"
import {DateRangeService} from "@/DateRangeService"

@Component
export default class SankeyDiagram extends Vue {
  @Prop()
  dateRangeSelector?: DateRangeSelector

  @Prop({default: null})
  planId: string | null

  @Prop({default: null})
  startDate: Date | null

  @Inject()
  planService: PlanService

  @Inject()
  dateRangeService: DateRangeService

  chart: am4charts.SankeyDiagram | null
  lastClickedNode: SankeyNode | null

  private resultSet: any

  async created() {
    this.$watch(
      () => this.effectivePlanId,
      async () => {
        await this.dateRangeService.setPlan(this.effectivePlanId!)
        this.resultSet = await this.runQuery()
        const copy = JSON.parse(JSON.stringify(this.resultSet.loadResponse.results[0].data))
        this.addSuffixToData(copy)
        this.chart = this.createChart(copy)

        this.addSuffixToData(this.resultSet.loadResponse.results[0].data)
        this.updateChart()
      }
    )

    this.$watch(
      () => this.effectiveStartDate,
      async () => {
        if (this.effectivePlanId != null) {
          this.resultSet = await this.runQuery()
          if (this.chart == null) {
            const copy = JSON.parse(JSON.stringify(this.resultSet.loadResponse.results[0].data))
            this.addSuffixToData(copy)
            this.chart = this.createChart(copy)
          }

          this.addSuffixToData(this.resultSet.loadResponse.results[0].data)
          this.updateChart()
        }
      },

      {immediate: true}
    )
  }

  get effectivePlanId(): string | undefined {
    return this.planId == null ? this.planService.current?.id : this.planId
  }

  get effectiveStartDate(): Date | undefined {
    return this.startDate == null ? this.dateRangeSelector?.selected?.start : this.startDate
  }

  private createChart(chartData: QueryData) {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.SankeyDiagram)

    const t = theme()

    const hitBehaviour = (event: {type: string; target: SankeyNode}) => {
      if (event.target.outgoingDataItems.length > 0) {
        this.turnEverythingOff()
        this.lastClickedNode = event.target
        chart.nodes.each((key, node) => {
          if (node === event.target) {
            node.background.fill = am4core.color(t.primaryColor)
            node.outgoingDataItems.values.forEach(item => {
              item.link.fill = am4core.color("#8bbae4")
              item.toNode.background.fill = am4core.color(t.primaryColor)
              item.link.fillOpacity = 1
              item.link.zIndex = 10
              item.show()
            })
          }
        })
        chart.validate()
      }
    }

    const hitLinkBehaviour = (event: {type: string; target: SankeyLink}) => {
      const fromNode = event.target.dataItem.fromNode
      hitBehaviour({type: "hit", target: fromNode})
    }

    chart.minNodeSize = 0.06
    chart.dataFields.fromName = SuperScoreDimension.score
    chart.dataFields.toName = SuperScoreDimension._scoreNow
    chart.dataFields.value = MemberSnapshotMeasure.countMembers
    chart.chartContainer.marginTop = 15
    chart.data = chartData

    const linksTemplate = chart.links.template
    linksTemplate.fillOpacity = 0.3
    linksTemplate.adapter.add(
      "tooltipText",
      (value, target) => `{value.value} members moved from a score of
      ${target.dataItem.fromName.slice(0, -1)} to ${target.dataItem.toName.slice(0, -1)}`
    )

    linksTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer

    linksTemplate.events.on("hit", hitLinkBehaviour)

    const nodeTemplate = chart.nodes.template

    nodeTemplate.adapter.add("tooltipText", (value, target) => {
      if (target.outgoingDataItems.values.length > 0) {
        let total = 0
        target.outgoingDataItems.values.forEach(
          outgoingDataItem => (total += outgoingDataItem.value)
        )

        return `${total} members`
      } else return ""
    })

    nodeTemplate.fill = am4core.color("#d9d9d9")
    nodeTemplate.events.off("hit")
    nodeTemplate.events.on("hit", hitBehaviour)

    nodeTemplate.width = 40
    nodeTemplate.nameLabel.locationX = 0.2
    nodeTemplate.nameLabel.label.fontWeight = "bold"
    nodeTemplate.nameLabel.label.fill = am4core.color("#fff")

    nodeTemplate.draggable = false
    nodeTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer

    nodeTemplate.adapter.add("name", value => value?.slice(0, -1))

    chart.events.once("appeared", event => {
      // FIXME this paints correctly, however it only happens after the
      // animation, which makes it look late visually. The colour needs to be
      // set before the animation starts so the animation paints the highlighted
      // node
      const mostMovedItem = event.target.dataItems.values
        .filter(
          v =>
            !v.fromName.startsWith(v.toName.slice(0, -1)) &&
            !v.toName.startsWith(v.fromName.slice(0, -1))
        )
        .reduce((a, b) => (a.values.value.value >= b.values.value.value ? a : b))
      mostMovedItem.fromNode.dispatch("hit")
      if (!this.interactive) {
        mostMovedItem.link.showTooltipOn = "always"
      }
    })

    return chart
  }

  private updateChart() {
    if (this.chart !== null) {
      keyedDataUpdate(
        MemberSnapshotMeasure.countMembers,
        (row: any) => toKey(row, [SuperScoreDimension.score, SuperScoreDimension._scoreNow]),
        this.resultSet.loadResponse.results[0].data,
        this.chart.data
      )

      this.chart.invalidateRawData()
    }
  }

  private turnEverythingOff() {
    this.chart!.nodes.each((key, node) => {
      node.background.fill = am4core.color("#d9d9d9")
      node.show()
    })
    this.chart!.links.values.forEach(link => {
      link.fill = am4core.color("#d9d9d9")
      link.fillOpacity = 0.3
      link.zIndex = 1
      link.show()
    })
  }

  private async runQuery() {
    const measures = [MemberSnapshotMeasure.countMembers]
    const dimensions = [SuperScoreDimension.score, SuperScoreDimension._scoreNow]
    const order: {[Key in Dimension]?: QueryOrderDirection} = {
      [SuperScoreDimension.score]: "asc",
    }
    const yearMonth: string = format(this.effectiveStartDate!, "yyyyMM")

    const filters = [
      {
        dimension: "Member.planId",
        operator: "equals",
        values: [this.effectivePlanId!],
      },
      {
        dimension: "MemberSnapshot.atYearMonth",
        operator: "equals",
        values: [yearMonth],
      },
    ]

    const query = {measures, dimensions, filters, order}
    return cubejsApi.load(query)
  }

  private addSuffixToData(dataSet: Array<Record<string, string>>) {
    dataSet.forEach(superScoreChange => {
      superScoreChange[SuperScoreDimension.score] = `${
        superScoreChange[SuperScoreDimension.score]
      }a`
      superScoreChange[SuperScoreDimension._scoreNow] = `${
        superScoreChange[SuperScoreDimension._scoreNow]
      }b`
    })
  }

  get interactive(): boolean {
    return this.planId === null && this.startDate === null
  }
}
